/**
 * Regulatory Database - admin workflow, topics, revision, audit, version-diff and coverage helpers.
 * Mirrors policylens-backend regulatoryContent.controller.ts (commit 0632a76) and the taxonomy in
 * services/regulatory/taxonomy.ts. The taxonomy VOCABULARY comes from GET /regulatory-content/sources; this file
 * holds no topic list of its own.
 */
import { userMessageForError } from './apiError';

export const ADMIN_API = Object.freeze({
    dismiss: (id) => `/regulatory-content/${id}/dismiss`,
    restore: (id) => `/regulatory-content/${id}/restore`,
    clearSupersession: (id) => `/regulatory-content/${id}/clear-supersession`,
    revise: (id) => `/regulatory-content/${id}/revise`,
    reviseCancel: (id) => `/regulatory-content/${id}/revise/cancel`,
    audit: (id) => `/regulatory-content/${id}/audit`,
    coverage: '/regulatory-content/coverage',
});

/** A 404/405 from a genuinely optional endpoint reads as absence of the feature, not as a broken screen. */
export function describeUnavailable(err, what) {
    if (err?.status === 404 || err?.status === 405) return `${what} is not available on this server yet.`;
    return userMessageForError(err);
}

const errorCode = (err) => err?.details?.code || err?.code;

// ---- workflow status ---------------------------------------------------------------------------------------
// Server `workflowStatus` (mutually exclusive): dismissed > unpublished (withdrawn) > published > awaiting_review > needs_text | ready_to_draft.
export const STATE_LABELS = Object.freeze({
    needs_text: 'Needs text',
    ready_to_draft: 'Ready to draft',
    awaiting_review: 'Awaiting review',
    published: 'Published',
    unpublished: 'Withdrawn',
    dismissed: 'Dismissed',
});
export const STATE_COLORS = Object.freeze({ needs_text: 'default', ready_to_draft: 'blue', awaiting_review: 'orange', published: 'green', unpublished: 'orange', dismissed: 'default' });
export const STATE_FILTERS = Object.freeze(Object.keys(STATE_LABELS));

/** The row's workflow status as the server computed it; null (shown as plain "Unpublished") if absent or unknown. */
export const rowState = (r) => (r?.workflowStatus && STATE_LABELS[r.workflowStatus] ? r.workflowStatus : null);

// ---- topics (taxonomy) -------------------------------------------------------------------------------------
/** taxonomy {version, domains:[{id,title}], topics:[{id,domainId,title}]} -> Select options labelled "Domain: Topic". */
export function topicOptions(taxonomy) {
    const domainTitle = new Map((taxonomy?.domains || []).map((d) => [d.id, d.title]));
    return (taxonomy?.topics || []).map((t) => ({ value: t.id, label: `${domainTitle.get(t.domainId) || t.domainId}: ${t.title}` }));
}
export const domainOptions = (taxonomy) => (taxonomy?.domains || []).map((d) => ({ value: d.id, label: d.title }));
export const topicTitleMap = (taxonomy) => Object.fromEntries((taxonomy?.topics || []).map((t) => [t.id, t.title]));

/** Reader facets from items already returned by the API (readers have no taxonomy route): topics/domains that have published items. */
export function topicFacets(items) {
    const topics = new Map();
    const domains = new Map();
    (items || []).forEach((it) => (it.topics || []).forEach((t) => {
        topics.set(t.id, `${t.domainTitle}: ${t.title}`);
        domains.set(t.domainId, t.domainTitle);
    }));
    return {
        topics: [...topics].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label)),
        domains: [...domains].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label)),
    };
}

// ---- list input helpers ------------------------------------------------------------------------------------
export const parseCommaList = (text) => String(text || '').split(',').map((s) => s.trim()).filter(Boolean);
export const sameList = (a, b) => JSON.stringify([...(a || [])].sort()) === JSON.stringify([...(b || [])].sort());

/** Topic ids of an admin row/item (rows carry topics[] objects). */
export const topicIdsOf = (item) => (item?.topics || []).map((t) => t.id);

/**
 * PATCH body for the edit modal: only fields that changed. RBI title/text/reference are feed-owned and never sent.
 */
export function buildEditPatch(item, next, { ddMmYyyyToIso }) {
    const body = {};
    const feedOwned = item.sourceName === 'rbi';
    if (!feedOwned && next.title.trim() !== item.title) body.title = next.title.trim();
    if (!feedOwned && next.text !== (item.text || '')) body.text = next.text;
    const ref = next.officialDocumentNumber.trim();
    if (!feedOwned && ref && ref !== (item.officialDocumentNumber || '')) body.officialDocumentNumber = ref;
    const url = (next.sourceUrl || '').trim();
    if (!feedOwned && url && url !== (item.sourceUrl || '')) body.sourceUrl = url;
    const eff = next.effectiveDate.trim();
    const curEff = item.effectiveDate ? item.effectiveDate.slice(0, 10) : '';
    if (eff === '' && curEff) body.effectiveDate = null;
    else if (eff) {
        const iso = ddMmYyyyToIso(eff);
        if (iso && iso !== curEff) body.effectiveDate = iso;
    }
    const scope = parseCommaList(next.jurisdictionScope);
    if (!sameList(scope, item.jurisdictionScope || [])) body.jurisdictionScope = scope;
    if (!sameList(next.industryApplicability, item.industryApplicability || [])) body.industryApplicability = next.industryApplicability;
    const std = next.relatedStandardId || null;
    if (std !== (item.relatedStandardId || null)) body.relatedStandardId = std;
    if (!sameList(next.topicCodes, topicIdsOf(item))) body.topicCodes = next.topicCodes;
    return body;
}

// ---- source revision of an approved item -------------------------------------------------------------------
export const REVISION_FIELDS = Object.freeze(['title', 'text', 'officialDocumentNumber', 'authorityName', 'documentType', 'sourceUrl', 'publishedDate']);

/** POST /revise body: the reason plus only the fields the admin actually changed (publishedDate as ISO). */
export function buildReviseBody(item, form, { ddMmYyyyToIso, formatDdMmYyyy }) {
    const body = { reason: form.reason.trim() };
    ['title', 'officialDocumentNumber', 'sourceUrl'].forEach((k) => {
        const v = (form[k] || '').trim();
        if (v && v !== (item[k] || '')) body[k] = v;
    });
    if (form.text && form.text !== (item.text || '')) body.text = form.text;
    ['authorityName', 'documentType'].forEach((k) => {
        if (form[k] && form[k] !== item[k]) body[k] = form[k];
    });
    const pd = (form.publishedDate || '').trim();
    if (pd && pd !== formatDdMmYyyy(item.publishedAt)) {
        const iso = ddMmYyyyToIso(pd);
        if (iso) body.publishedDate = iso;
    }
    return body;
}
export const reviseHasChanges = (body) => Object.keys(body).some((k) => k !== 'reason');

export function describeReviseError(err) {
    const code = errorCode(err);
    if (err?.status === 409 && code === 'NOT_APPROVED') return 'Only an approved document can be revised. Edit it directly instead.';
    if (err?.status === 409 && code === 'REVISION_PENDING') return 'A revision is already pending for this document. Draft it or cancel it first.';
    if (err?.status === 409 && code === 'DUPLICATE') return 'Another document already has this authority and reference number.';
    return userMessageForError(err);
}

export function describeDismissError(err) {
    const code = errorCode(err);
    if (err?.status === 409 && code === 'HAS_APPROVED') return 'A document with an approved summary cannot be dismissed. Unpublish or supersede it instead.';
    if (err?.status === 409 && code === 'HAS_PENDING_DRAFT') return 'A draft is waiting for review. Reject it first, then dismiss.';
    if (err?.status === 409 && code === 'ALREADY_DISMISSED') return 'This document is already dismissed.';
    if (err?.status === 409 && code === 'NOT_DISMISSED') return 'This document is not dismissed.';
    return userMessageForError(err);
}

// ---- review-card source search ----------------------------------------------------------------------------
export function countMatches(text, term) {
    const t = String(term || '').trim().toLowerCase();
    if (!t) return 0;
    const hay = String(text || '').toLowerCase();
    let n = 0;
    for (let i = hay.indexOf(t); i !== -1; i = hay.indexOf(t, i + t.length)) n += 1;
    return n;
}

/** Splits text into [{text, hit}] segments around case-insensitive matches (capped so a huge document cannot freeze the page). */
export function highlightSegments(text, term, cap = 500) {
    const t = String(term || '').trim().toLowerCase();
    const src = String(text || '');
    if (!t) return [{ text: src, hit: false }];
    const hay = src.toLowerCase();
    const out = [];
    let pos = 0;
    let seen = 0;
    for (let i = hay.indexOf(t); i !== -1 && seen < cap; i = hay.indexOf(t, pos)) {
        if (i > pos) out.push({ text: src.slice(pos, i), hit: false });
        out.push({ text: src.slice(i, i + t.length), hit: true });
        pos = i + t.length;
        seen += 1;
    }
    if (pos < src.length) out.push({ text: src.slice(pos), hit: false });
    return out;
}

// ---- version diff ------------------------------------------------------------------------------------------
const obligationLine = (o) => `${o.action} | ${o.deadline || 'no deadline'} | "${o.quote}"`;
const diffSets = (a, b) => ({ removed: a.filter((x) => !b.includes(x)), added: b.filter((x) => !a.includes(x)) });

/** What changed between two approved drafts (older -> newer), as plain added/removed line lists. */
export function diffDrafts(older, newer) {
    const o = older || {};
    const n = newer || {};
    const flatMap = (d) => Object.entries(d.crossMappings || {}).flatMap(([std, ids]) => (ids || []).map((id) => `${std}: ${id}`));
    return {
        summary: diffSets(o.summaryBullets || [], n.summaryBullets || []),
        obligations: diffSets((o.obligations || []).map(obligationLine), (n.obligations || []).map(obligationLine)),
        crossMappings: diffSets(flatMap(o), flatMap(n)),
        applicability: (o.applicability || '') === (n.applicability || '') ? null : { from: o.applicability || '', to: n.applicability || '' },
    };
}
export const diffIsEmpty = (d) => !d.applicability && ['summary', 'obligations', 'crossMappings'].every((k) => d[k].added.length === 0 && d[k].removed.length === 0);

// ---- audit trail -------------------------------------------------------------------------------------------
const brief = (v) => {
    if (v === null || v === undefined || v === '') return '';
    if (typeof v === 'string') return v;
    try { return JSON.stringify(v); } catch { return String(v); }
};
export const AUDIT_PAGE = 25;
/** GET /regulatory-content/:id/audit -> entries[{timestamp, action, outcome, actorEmail, resourceType, resourceId, httpRequestId, details, source}], newest first. */
export function normaliseAuditEntries(data) {
    const entries = Array.isArray(data?.entries) ? data.entries : [];
    return entries.map((e, i) => ({
        key: `${e.timestamp || ''}-${e.action || ''}-${i}`,
        action: e.action || '',
        outcome: e.outcome || '',
        actor: e.actorEmail || '',
        resource: [e.resourceType, e.resourceId].filter(Boolean).join(' '),
        ts: e.timestamp || null,
        details: brief(e.details),
        requestId: e.httpRequestId || '',
    }));
}

// ---- coverage (row 10 taxonomy) ----------------------------------------------------------------------------
/**
 * GET /regulatory-content/coverage -> { taxonomyVersion, totals, domains:[{id,title,publishedCount,topicsWithoutPublishedRequirement,
 * topics:[{id,title,publishedCount,awaitingReviewCount,noPublishedRequirementYet}]}], note }. Counts are items, not obligations.
 */
export function normaliseCoverage(data) {
    const domains = (Array.isArray(data?.domains) ? data.domains : []).map((d) => ({
        id: String(d.id),
        title: d.title || String(d.id),
        publishedCount: Number.isFinite(d.publishedCount) ? d.publishedCount : 0,
        uncoveredCount: Number.isFinite(d.topicsWithoutPublishedRequirement) ? d.topicsWithoutPublishedRequirement : 0,
        topics: (Array.isArray(d.topics) ? d.topics : []).map((t) => ({
            id: String(t.id),
            title: t.title || String(t.id),
            publishedCount: Number.isFinite(t.publishedCount) ? t.publishedCount : 0,
            awaitingReviewCount: Number.isFinite(t.awaitingReviewCount) ? t.awaitingReviewCount : 0,
            noPublishedRequirementYet: t.noPublishedRequirementYet === true || !(t.publishedCount > 0),
        })),
    }));
    return { version: data?.taxonomyVersion ?? null, totals: data?.totals || null, note: data?.note || '', domains };
}

// ---- project grouping --------------------------------------------------------------------------------------
/**
 * byTopic[{topic:{id,title,domainId,domainTitle}, itemIds}] + unclassifiedItemIds -> domain groups for display.
 * Titles are resolved only from the items on the current page; ids on other pages are counted, never named.
 */
export function groupByTopic(byTopic, unclassifiedItemIds, items) {
    const titleOf = new Map((items || []).map((i) => [i.id, i.title]));
    const domains = new Map();
    (byTopic || []).forEach(({ topic, itemIds }) => {
        if (!domains.has(topic.domainId)) domains.set(topic.domainId, { id: topic.domainId, title: topic.domainTitle, topics: [] });
        domains.get(topic.domainId).topics.push({ id: topic.id, title: topic.title, count: itemIds.length, names: itemIds.filter((id) => titleOf.has(id)).map((id) => titleOf.get(id)), offPage: itemIds.filter((id) => !titleOf.has(id)).length });
    });
    const un = unclassifiedItemIds || [];
    return {
        domains: [...domains.values()],
        unclassified: { count: un.length, names: un.filter((id) => titleOf.has(id)).map((id) => titleOf.get(id)), offPage: un.filter((id) => !titleOf.has(id)).length },
    };
}
